import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Schema.org JSON-LD injector. Pass a single object or an array of typed objects.
 * Handles serialization safely (escapes </script>).
 */
export default function JsonLd({ data }) {
  if (!data) return null;
  const json = JSON.stringify(data).replace(/</g, "\\u003c");
  return (
    <Helmet>
      <script type="application/ld+json">{json}</script>
    </Helmet>
  );
}

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export const orgSchema = (siteName = "Biscayne Quilt Supply") => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: siteName,
  url: ORIGIN,
  logo: `${ORIGIN}/favicon.ico`,
  sameAs: [],
});

export const websiteSchema = (siteName = "Biscayne Quilt Supply") => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteName,
  url: ORIGIN,
  potentialAction: {
    "@type": "SearchAction",
    target: `${ORIGIN}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const productSchema = (product, url) => {
  if (!product) return null;
  const inStock = (product.stock || 0) > 0 || product.allow_backorder;
  const offer = {
    "@type": "Offer",
    url: url || ORIGIN,
    priceCurrency: product.currency || "USD",
    price: Number(product.price || 0).toFixed(2),
    availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
  };
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.short_description || product.seo_description || product.description?.slice(0, 500) || product.title,
    image: product.images?.length ? product.images : (product.primary_image ? [product.primary_image] : []),
    sku: product.sku || product.asin || product.id,
    ...(product.asin && { gtin: product.asin }),
    ...(product.brand?.name && {
      brand: { "@type": "Brand", name: product.brand.name },
    }),
    offers: offer,
  };
};

export const breadcrumbSchema = (crumbs) => {
  if (!crumbs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url ? `${ORIGIN}${c.url}` : undefined,
    })),
  };
};

export const faqSchema = (items) => {
  if (!items?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
};
